import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PublicReferral from "@/pages/PublicReferral";
import MagicVerify from "@/pages/MagicVerify";
import Impressum from "@/pages/Impressum";
import Datenschutz from "@/pages/Datenschutz";

import CustomerDashboard from "@/pages/customer/Dashboard";
import Empfehlungen from "@/pages/customer/Empfehlungen";
import EmpfehlungsBaum from "@/pages/customer/EmpfehlungsBaum";
import Trophaeen from "@/pages/customer/Trophaeen";
import CoinVerlauf from "@/pages/customer/CoinVerlauf";
import Profil from "@/pages/customer/Profil";
import ServiceTickets from "@/pages/customer/ServiceTickets";
import TippgeberCockpit from "@/pages/customer/TippgeberCockpit";
import Shop from "@/pages/customer/Shop";

import Kanban from "@/pages/sales/Kanban";
import VertrieblerStats from "@/pages/sales/VertrieblerStats";
import VertrieblerLeaderboard from "@/pages/sales/VertrieblerLeaderboard";
import EmpfehlungDetail from "@/pages/sales/EmpfehlungDetail";
import TechnikerKalender from "@/pages/sales/TechnikerKalender";

import Kooperationspartner from "@/pages/customer/Kooperationspartner";
import ProgrammRegeln from "@/pages/customer/ProgrammRegeln";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import SignupApprovals from "@/pages/admin/SignupApprovals";
import GewerbeApprovals from "@/pages/admin/GewerbeApprovals";
import Vollzahlungen from "@/pages/admin/Vollzahlungen";
import Provisionen from "@/pages/admin/Provisionen";
import Gutschriften from "@/pages/admin/Gutschriften";
import GeschenkeAdmin from "@/pages/admin/GeschenkeAdmin";
import ShopAdmin from "@/pages/admin/ShopAdmin";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminEmpfehlungen from "@/pages/admin/AdminEmpfehlungen";
import KooperationspartnerAdmin from "@/pages/admin/KooperationspartnerAdmin";
import ServiceAdmin from "@/pages/admin/ServiceAdmin";

function RoleRoot() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin" || user.role === "buchhalter") return <Navigate to="/admin" replace />;
  if (user.role === "vertriebler") return <Navigate to="/vertriebler" replace />;
  if (user.tippgeber_modus === "gewerblich_aktiv") return <Navigate to="/app/tippgeber" replace />;
  return <Navigate to="/app" replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/passwort-vergessen" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/r/:slug" element={<PublicReferral />} />
          <Route path="/auth/magic/:token" element={<MagicVerify />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/datenschutz" element={<Datenschutz />} />

          {/* Customer */}
          <Route path="/app" element={<ProtectedRoute roles={["kunde"]}><Layout><CustomerDashboard /></Layout></ProtectedRoute>} />
          <Route path="/app/empfehlen" element={<ProtectedRoute roles={["kunde"]}><Layout><Empfehlungen /></Layout></ProtectedRoute>} />
          <Route path="/app/baum" element={<ProtectedRoute roles={["kunde"]}><Layout><EmpfehlungsBaum /></Layout></ProtectedRoute>} />
          <Route path="/app/trophaeen" element={<ProtectedRoute roles={["kunde"]}><Layout><Trophaeen /></Layout></ProtectedRoute>} />
          <Route path="/app/shop" element={<ProtectedRoute roles={["kunde"]}><Layout><Shop /></Layout></ProtectedRoute>} />
          <Route path="/app/coins" element={<ProtectedRoute roles={["kunde"]}><Layout><CoinVerlauf /></Layout></ProtectedRoute>} />
          <Route path="/app/profil" element={<ProtectedRoute roles={["kunde"]}><Layout><Profil /></Layout></ProtectedRoute>} />
          <Route path="/app/service" element={<ProtectedRoute roles={["kunde"]}><Layout><ServiceTickets /></Layout></ProtectedRoute>} />
          <Route path="/app/tippgeber" element={<ProtectedRoute roles={["kunde"]}><Layout><TippgeberCockpit /></Layout></ProtectedRoute>} />
          <Route path="/app/partner" element={<ProtectedRoute roles={["kunde"]}><Layout><Kooperationspartner /></Layout></ProtectedRoute>} />
          <Route path="/app/regeln" element={<ProtectedRoute roles={["kunde"]}><Layout><ProgrammRegeln /></Layout></ProtectedRoute>} />

          {/* Sales */}
          <Route path="/vertriebler" element={<ProtectedRoute roles={["vertriebler"]}><Layout><Kanban /></Layout></ProtectedRoute>} />
          <Route path="/vertriebler/empfehlung/:id" element={<ProtectedRoute roles={["vertriebler", "admin"]}><Layout><EmpfehlungDetail /></Layout></ProtectedRoute>} />
          <Route path="/vertriebler/stats" element={<ProtectedRoute roles={["vertriebler"]}><Layout><VertrieblerStats /></Layout></ProtectedRoute>} />
          <Route path="/vertriebler/leaderboard" element={<ProtectedRoute roles={["vertriebler"]}><Layout><VertrieblerLeaderboard /></Layout></ProtectedRoute>} />
          <Route path="/vertriebler/profil" element={<ProtectedRoute roles={["vertriebler"]}><Layout><Profil /></Layout></ProtectedRoute>} />
          <Route path="/vertriebler/kalender" element={<ProtectedRoute roles={["vertriebler", "admin"]}><Layout><TechnikerKalender /></Layout></ProtectedRoute>} />

          {/* Admin */}
          <Route path="/admin" element={<ProtectedRoute roles={["admin", "buchhalter"]}><Layout><AdminDashboard /></Layout></ProtectedRoute>} />
          <Route path="/admin/signups" element={<ProtectedRoute roles={["admin"]}><Layout><SignupApprovals /></Layout></ProtectedRoute>} />
          <Route path="/admin/gewerbe" element={<ProtectedRoute roles={["admin"]}><Layout><GewerbeApprovals /></Layout></ProtectedRoute>} />
          <Route path="/admin/empfehlungen" element={<ProtectedRoute roles={["admin"]}><Layout><AdminEmpfehlungen /></Layout></ProtectedRoute>} />
          <Route path="/admin/vollzahlungen" element={<ProtectedRoute roles={["admin", "buchhalter"]}><Layout><Vollzahlungen /></Layout></ProtectedRoute>} />
          <Route path="/admin/provisionen" element={<ProtectedRoute roles={["admin", "buchhalter"]}><Layout><Provisionen /></Layout></ProtectedRoute>} />
          <Route path="/admin/gutschriften" element={<ProtectedRoute roles={["admin", "buchhalter"]}><Layout><Gutschriften /></Layout></ProtectedRoute>} />
          <Route path="/admin/geschenke" element={<ProtectedRoute roles={["admin"]}><Layout><GeschenkeAdmin /></Layout></ProtectedRoute>} />
          <Route path="/admin/shop" element={<ProtectedRoute roles={["admin"]}><Layout><ShopAdmin /></Layout></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute roles={["admin", "buchhalter"]}><Layout><AdminUsers /></Layout></ProtectedRoute>} />
          <Route path="/admin/leaderboard" element={<ProtectedRoute roles={["admin", "buchhalter"]}><Layout><VertrieblerLeaderboard /></Layout></ProtectedRoute>} />
          <Route path="/admin/partner" element={<ProtectedRoute roles={["admin"]}><Layout><KooperationspartnerAdmin /></Layout></ProtectedRoute>} />
          <Route path="/admin/kalender" element={<ProtectedRoute roles={["admin", "buchhalter"]}><Layout><TechnikerKalender /></Layout></ProtectedRoute>} />
          <Route path="/admin/service" element={<ProtectedRoute roles={["admin", "buchhalter"]}><Layout><ServiceAdmin /></Layout></ProtectedRoute>} />

          <Route path="/" element={<RoleRoot />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
